import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Stack, Typography } from '@mui/material';
import theme from '@src/theme';
import OrganisationImageFallback from '@src/components/organisation/OrganisationImageFallback';
import { Routes } from '@src/navigation/Routes';
import { generatePath, Link } from 'react-router-dom';
import EmptyState from '@src/components/utilities/EmptyState/EmptyState';
import { OrganisationSearchResultDTO } from '@src/adapters/hooks/organisation/useSearchOrganisation';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';

const PREFIX = 'Organisations';

const classes = {
  root: `${PREFIX}-root`,
  table: `${PREFIX}-table`,
  tableCell: `${PREFIX}-tableCell`,
  header: `${PREFIX}-header`,
  link: `${PREFIX}-link`,
};

const Root = styled('div')(({ theme: Theme }) => ({
  [`&.${classes.root}`]: {
    width: '100%',
  },

  [`& .${classes.table}`]: {
    width: '100%',
  },

  [`& .${classes.tableCell}`]: {
    paddingLeft: '16px',
  },
}));

interface Props {
  organisations?: OrganisationSearchResultDTO[];
  selectedPage: number;
  rowsPerPage: number;
  recordCount: number;
  changePage(value: number): void;
  changeRowsPerPage(value: number): void;
}

function OrganisationTable({
  organisations,
  selectedPage,
  rowsPerPage,
  recordCount,
  changePage: setPage,
  changeRowsPerPage: setRowsPerPage,
}: Props) {
  const { setCurrentOrganisation } = useNavigationStore();
  const isEmpty = !organisations || organisations.length === 0;

  return (
    <Root className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="Contents table">
          <TableHead className={classes.header}>
            <TableRow>
              <TableCell>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.text.secondary}>
                  Organisation Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.text.secondary}>
                  Short Code
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="subtitle1"
                  color={theme.palette.text.secondary}>
                  Code Set
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <EmptyState title="No organisations to show!" />
                </TableCell>
              </TableRow>
            ) : (
              organisations.map((organisation) => (
                <TableRow key={organisation.id}>
                  <TableCell>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      p={1}>
                      <OrganisationImageFallback
                        src={organisation.logoUrl}
                        alt={`${organisation.name} Image`}
                      />

                      <Link
                        to={generatePath(Routes.OrganisationHospitals, {
                          organisationSlug: organisation?.name ?? '',
                        })}
                        onClick={() =>
                          setCurrentOrganisation({
                            id: organisation.id,
                            name: organisation.name,
                            isActive: organisation.isActive,
                          })
                        }
                        style={{
                          textDecoration: 'none',
                          color: theme.palette.primary.main,
                        }}>
                        <Typography variant="subtitle2">
                          {organisation.name}
                        </Typography>
                      </Link>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}>
                      {organisation.shortCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}>
                      {organisation.codeSet}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEmpty && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={recordCount}
          rowsPerPage={rowsPerPage}
          page={selectedPage}
          onPageChange={(event, number) => setPage(number)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </Root>
  );
}

export default OrganisationTable;
